/**
 *
 *  Offers page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 07/08/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import BreadcrumbNav from "../BreadcrumbNav/BreadcrumbNav";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils

// Icons
import CheckNiceIcon from "../../icons/CheckboxOIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Images

export default function OfferPage() {
  const { OfferId } = useParams();

  const { theme } = useTheme();

  const {
    user,
    offers,
    customerMembership,
    loadCustomerMembership,
    loadOffers,
    clearCustomerMembership,
    customerMembershipLoaded,
  } = useAppState();

  const [offerId, setOfferId] = useState(null);
  const [offer, setOffer] = useState(null);

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);

  const isUserLoggedIn = !!(user && user.id);

  useEffect(
    () => {
      loadOffers();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (user) {
      loadCustomerMembership(user.id).catch((err) => {
        console.log("Error loading customer membership: ", err);
      });
    }
  }, [user]);

  // For logout
  useEffect(() => () => clearCustomerMembership(), []);

  useEffect(() => {
    if (OfferId && offers && offers.length) {
      setOfferId(OfferId);

      const offer = offers.find((val) => val.id == OfferId);

      if (offer) {
        setOffer(offer);
      }
    } else {
      setOfferId(null);
    }
  }, [user, OfferId, offers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sliderRef.current && offer) {
      setTimeout(() => {
        // Check if it's present, because we might be on different state already
        sliderRef.current &&
          sliderRef.current.scrollTo({
            left: sliderRef.current.scrollWidth,
            behavior: "smooth",
          });
        // sliderRef.current.scrollLeft = sliderRef.current.scrollWidth;
      }, 1000);
    }
  }, [sliderRef.current, offer]);

  var formattedDescription = "";

  if (offer) {
    const lines = offer.description.split("\n");

    formattedDescription = lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  }

  return (
    <>
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content={`On-demand, in person, personal training in Los Angeles`}
      />
      <Header behaviour="none" isLoggedIn={isUserLoggedIn} heroRef={heroRef} />

      <div />

      <div className="tbk-overflow-hidden">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div
            ref={sliderRef}
            className="tb-slider tbk-mt-3 tbk-mb-3 tbk-flex tbk-flex-nowrap tbk-items-center"
          >
            <BreadcrumbNav to={"/lifestyle"}>
              Lifestyle collective
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav>{offer && offer.title}</BreadcrumbNav>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 lg:tbk-pt-4 lg:tbk-pb-4">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="tbk-grid tbk-gap-4 lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-7 lg:tbk-mb-0">
              <div className="tbk-relative tbk-mb-4 tbk-flex tbk-items-center tbk-p-1">
                <div className="tbk-ml-0 tbk-mr-2 tbk-shrink-0">
                  <img
                    className="tbk-h-7 tbk-w-7"
                    src={offer && offer.logo}
                    alt={offer && offer.title}
                  />
                </div>
                <div className="tbk-min-w-0 tbk-grow tbk-overflow-hidden">
                  <h1
                    className={`tbk-text-h3-subtitle lg:tbk-text-h2-black tbk-mb-0.5 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-left tbk-text-primary`}
                  >
                    {offer && offer.title}
                  </h1>
                  <div className="tbk-text-small tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-grey">
                    {offer && offer.subtitle}
                  </div>
                </div>
              </div>

              {offer && offer.code && customerMembership ? (
                <>
                  <div className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-mb-1 tbk-text-primary lg:tbk-mb-1 ">
                    <span className="tbk-text-blue-grey">Your Code:</span>{" "}
                    {offer && offer.code}
                  </div>
                  <div className="tbk-text-main lg:tbk-text-main tbk-mb-2 tbk-text-blue-grey lg:tbk-mb-4">
                    Redeem this code when making a purchase directly on the
                    partner's website
                  </div>
                </>
              ) : !customerMembership ? (
                <div className="tbk-text-main lg:tbk-text-main tbk-mb-2 tbk-text-blue-grey lg:tbk-mb-4">
                  Join the Lifestyle Collective to access this discount.
                </div>
              ) : null}

              <div className="tbk-text-main lg:tbk-text-main tbk-overflow-hidden_ tbk-text-ellipsis_ tbk-whitespace-nowrap_ tbk-mb-1 tbk-text-basic-trueblack lg:tbk-mb-2">
                {formattedDescription}
              </div>

              <div className="tbk-mt-4 tbk-hidden lg:tbk-mt-4 lg:tbk-mb-0 lg:tbk-block">
                <a href={offer && offer.url} target="_blank">
                  <Button
                    className="tbk-inline-flex tbk-w-72 tbk-pl-5 tbk-pr-5"
                    endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
                  >
                    Visit the Website
                  </Button>
                </a>
              </div>
            </div>

            <div className="tbk-relative tbk--order-1 tbk--mr-2 tbk--ml-2 tbk-overflow-hidden tbk-bg-home-our-coaches-mobile lg:tbk-order-1 lg:tbk-mr-0 lg:tbk-ml-0 lg:tbk-h-auto lg:tbk-bg-none">
              <div className="tbk-relative">
                <img
                  className="tbk-w-full tbk-object-cover lg:tbk-rounded-2xl"
                  src={offer && offer.coverPhoto}
                  alt={offer && offer.title}
                  // alt={trainer.name}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-pt-4 tbk-pb-7 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <Link to={"/lifestyle"}>
            <Button
              className="tbk-mt-4 tbk-w-32 tbk-text-primary"
              variant="secondary"
              size={"md"}
              startIcon={<ArrowLeftIcon className="tbk-tex-primary" />}
            >
              Back
            </Button>
          </Link>
        </div>
      </div>

      <div className="tbk-sticky tbk-bottom-0 tbk-z-10 tbk-rounded-t-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube lg:tbk-hidden">
        <a href={offer && offer.url} target="_blank">
          <Button
            className="tbk-mb-2 tbk-w-full tbk-pl-5 tbk-pr-5"
            endIcon={<ArrowRightIcon className="" />}
          >
            Visit the Website
          </Button>
        </a>
      </div>

      <Footer />
    </>
  );
}
